import { type HTMLAttributes } from 'react'
import { cn } from './cn'

type Size = 'xs' | 'sm' | 'md' | 'lg'

const sizeMap: Record<Size, string> = {
  xs: 'h-5  w-5  text-[9px]',
  sm: 'h-6  w-6  text-[10px]',
  md: 'h-8  w-8  text-xs',
  lg: 'h-10 w-10 text-sm',
}

export function initials(name?: string | null): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

type AvatarProps = HTMLAttributes<HTMLSpanElement> & {
  name?: string
  color?: string
  size?: Size
  src?: string
}

export function Avatar({ name, color, size = 'md', src, className, style, ...rest }: AvatarProps) {
  return (
    <span
      style={{
        backgroundColor: color ?? 'var(--color-bg-muted)',
        color: color ? '#fff' : 'var(--color-fg-muted)',
        ...style,
      }}
      className={cn(
        'inline-flex items-center justify-center rounded-full font-semibold border border-border-strong/10 select-none',
        sizeMap[size],
        className,
      )}
      title={name ?? undefined}
      {...rest}
    >
      {src ? <img src={src} alt={name ?? ''} className="h-full w-full rounded-full object-cover" /> : initials(name)}
    </span>
  )
}

type StackPerson = { name?: string | null; color?: string | null; src?: string | null }

type StackProps = {
  people: StackPerson[]
  max?: number
  size?: Size
  className?: string
}

export function AvatarStack({ people, max = 4, size = 'sm', className }: StackProps) {
  const shown = people.slice(0, max)
  const rest = people.length - shown.length
  return (
    <div className={cn('flex items-center', className)}>
      {shown.map((p, i) => (
        <span
          key={i}
          style={{ marginLeft: i === 0 ? 0 : -8, zIndex: max - i, position: 'relative' }}
          className="inline-block ring-2 ring-bg-elevated rounded-full"
        >
          <Avatar
            size={size}
            name={p.name ?? undefined}
            color={p.color ?? undefined}
            src={p.src ?? undefined}
          />
        </span>
      ))}
      {rest > 0 && (
        <span
          style={{ marginLeft: -8 }}
          className={cn(
            'inline-flex items-center justify-center rounded-full bg-bg-muted text-fg-muted ring-2 ring-bg-elevated font-semibold',
            sizeMap[size],
          )}
        >
          +{rest}
        </span>
      )}
    </div>
  )
}
